import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Link, Typography, Stack } from "@mui/material";
import { EGButton } from "../../../../../../MainComponents/Button/EGButton/EGButton";
import { EGBasicSelect } from "../../../../../../MainComponents/Select/EGBasicSelect";
import { EGInput } from "../../../../../../MainComponents/Input/EGInput";
import { useSnackbar } from "notistack";
import useUnsavedChangesWarning from "../../../../../../../hooks/useUnsavedChangesWarning";
import { useReportContext } from "../../../../../ReportContext/useReportContext";
import { createMeasure } from "../../../../../../../apis";
import { IconHeader } from "../../../../../../Icon/IconHader/IconHeader";
import { UnsavedChanges } from "../../../../../../Modal/Confirmation/UnsavedChanges/UnsavedChanges";
import { useReportId } from "../../../../../../../context/report/ReportContext";

export const MeasuresForm = ({
  closeForm,
  reportRender,
  getAllMeasures,
  measureCatalogOptions,
}) => {
  const [t] = useTranslation("report");
  const id = useReportId();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { measureOptions } = useReportContext();
  // Si el tenant tiene catálogo custom (RS-203104) se usan sus opciones;
  // si no, se mantienen las measureOptions hardcodeadas.
  const options = Array.isArray(measureCatalogOptions)
    ? measureCatalogOptions
    : measureOptions;

  const [addDetails, setAddDetails] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [measure, setMeasure] = useState("");
  const [another_measure, setAnother_measure] = useState("");
  const [comment, setComment] = useState("");
  // Show unsaved changes confirmation modal
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  // Detecta si el usuario capturó algo en el formulario
  const hasChanges = !!measure || !!another_measure || !!comment;
  // Prevent saving before leaving
  useUnsavedChangesWarning(hasChanges);

  useEffect(() => {
    // Scroll that adjusts to the form
    if (measure === "another") {
      setTimeout(() => {
        window.scrollTo({
          top: document.documentElement.scrollHeight,
          behavior: "smooth",
        });
      }, 100);
    }
  }, [measure]);

  // onSubmit
  const onSubmitMesure = async (e) => {
    e.preventDefault();
    const measureDataSend = {
      report_identifier: Number(id),
      measure,
      comment,
    };
    // Only add another_measure if it has a value
    if (measure === "another" && another_measure) {
      measureDataSend.another_measure = another_measure;
    }
    setIsLoading(true);

    const sending = enqueueSnackbar(`${t("Resolution.sending")}...`, {
      variant: "info",
      persist: true,
    });

    try {
      await createMeasure(measureDataSend).then((resp) => {
        if (resp.status === 201) {
          setIsLoading(false);
          setMeasure("");
          setComment("");
          setAnother_measure("");
          closeForm();
          reportRender();
          closeSnackbar(sending);
          getAllMeasures();
          enqueueSnackbar(t("Resolution.measure_added_successfully"), {
            variant: "success",
          });
        }
      });
    } catch (error) {
      console.error(error);
      setIsLoading(false);
      closeSnackbar(sending);
    }
  };
  // Limpia el formulario y lo cierra
  const resetAndClose = () => {
    setMeasure("");
    setAnother_measure("");
    setComment("");
    closeForm();
  };

  // Prevent saving before leaving
  const hdlCancel = () => {
    if (hasChanges) {
      setShowUnsavedModal(true);
      return;
    }
    resetAndClose();
  };

  // Confirmar salida descartando los cambios
  const hdlConfirmLeave = () => {
    setShowUnsavedModal(false);
    resetAndClose();
  };

  // Validation info
  const isInvalid =
    !measure ||
    (measure === "another" &&
      (another_measure.trim() === "" || another_measure.length < 3));

  return (
    <Box component="form">
      <IconHeader title={t("Resolution.corrective_preventive_measure")} />
      <Box my={1.5}>
        <EGBasicSelect
          options={options}
          value={measure}
          onChange={(e) => setMeasure(e.target.value)}
          label={`${t("Resolution.select_corrective_preventive_measure")}:`}
        />
        {measure === "another" && (
          <EGInput
            label={`${t("Resolution.new_corrective_preventive_measure")}:`}
            placeholder={t("Resolution.example_implement_monthly_audits")}
            sx={{ mt: 1.75 }}
            value={another_measure}
            onChange={(e) => setAnother_measure(e.target.value)}
            isValid={
              another_measure.length > 2 && another_measure.trim() !== ""
            }
            autoFocus
            helperText={t("Resolution.enter_new_corrective_preventive_measure")}
          />
        )}

        {measure !== "" && !addDetails && (
          <Typography textAlign="end" my={0.33}>
            <Link
              underline="hover"
              onClick={() => {
                setAddDetails(true);
                setTimeout(() => {
                  window.scrollTo({
                    top: document.documentElement.scrollHeight,
                    behavior: "smooth",
                  });
                }, 100);
              }}
            >
              {`+ ${t("Resolution.add_comment")}`}
            </Link>
          </Typography>
        )}

        {addDetails && (
          <EGInput
            sx={{ mt: 1.75, mb: 0.75 }}
            label={`${t("Resolution.details")}: ${comment.length}/500`}
            placeholder={t("Resolution.enter_details_related_selected_measure")}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            multiline
            autoFocus
            inputProps={{ maxLength: 500 }}
            rows={3.5}
          />
        )}
      </Box>
      <Stack
        display="flex"
        alignItems="center"
        justifyContent="end"
        flexDirection="row"
        flexWrap={{ xs: "wrap", sm: "nowrap" }}
        spacing={2}
        mt={1.5}
        mb={1}
      >
        <EGButton
          label={t("Resolution.save")}
          onClick={onSubmitMesure}
          loading={isLoading}
          iconName={isInvalid ? "mark_check" : "mark_check_bold"}
          disabled={isInvalid}
          color={isInvalid ? "light" : "success"}
          iconColor={isInvalid ? "" : "white"}
          iconColorHover={isInvalid ? "" : "white"}
          labelColor={isInvalid ? "" : "white"}
        />
        <EGButton
          iconName="close_icon"
          iconSize={21}
          label={t("Resolution.cancel")}
          onClick={hdlCancel}
          loading={isLoading}
        />
      </Stack>

      <UnsavedChanges
        open={showUnsavedModal}
        onClose={() => setShowUnsavedModal(false)}
        onCancel={() => setShowUnsavedModal(false)}
        onConfirm={hdlConfirmLeave}
      />
    </Box>
  );
};
